import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { vacationService, CreateVacationDTO, CreateVacationPeriodeDTO, AgentVacation } from '@/services/vacationService';
import { equipeService } from '@/services/equipeService';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  StopIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  MoonIcon,
  SunIcon,
  CalendarDaysIcon,
  UserIcon,
  Bars3Icon,
  ChartBarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { GanttChart } from '@/components/planning/GanttChart';

const STATUT_COLORS: Record<string, string> = {
  planifiee: 'bg-blue-100 text-blue-800 border-blue-200',
  en_cours: 'bg-orange-100 text-orange-800 border-orange-200',
  terminee: 'bg-green-100 text-green-800 border-green-200',
  annulee: 'bg-gray-100 text-gray-500 border-gray-200',
};

const STATUT_LABELS: Record<string, string> = {
  planifiee: 'Planifiee',
  en_cours: 'En cours',
  terminee: 'Terminee',
  annulee: 'Annulee',
};

const JOURS_SEMAINE = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
  { value: 7, label: 'Dim' },
];

export function PlanningPage() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [editingVacation, setEditingVacation] = useState<AgentVacation | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [displayMode, setDisplayMode] = useState<'table' | 'gantt'>('gantt');
  const [createMode, setCreateMode] = useState<'single' | 'period'>('single');
  const [selectedJours, setSelectedJours] = useState<number[]>([1, 2, 3, 4, 5]); // Lun-Ven par defaut

  // Calcul des dates pour la vue semaine
  const weekDates = useMemo(() => {
    const date = new Date(selectedDate);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.toISOString().split('T')[0];
    });
  }, [selectedDate]);

  // Chargement des vacations
  const { data: vacations, isLoading } = useQuery({
    queryKey: ['vacations', viewMode === 'day' ? selectedDate : weekDates[0], viewMode === 'day' ? selectedDate : weekDates[6]],
    queryFn: () =>
      viewMode === 'day'
        ? vacationService.list({ date: selectedDate })
        : vacationService.list({ date_debut: weekDates[0], date_fin: weekDates[6] }),
  });

  // Tous les agents de deneignement
  const { data: agentsData } = useQuery({
    queryKey: ['all-agents'],
    queryFn: () => equipeService.getAllAgents(),
  });
  const allAgents = agentsData?.agents;

  // Chefs d'equipe (ROLE_CHEF_DENEIGNEMENT) - role distinct de
  // ROLE_AGENT_DENEIGNEMENT (cf UserRepository::findByRole()), absents de
  // allAgents ci-dessus. Signale par l'utilisateur : un chef peut desormais
  // avoir une vacation (VacationController::peutAvoirVacation()) mais ce
  // formulaire ne proposait que les agents, pas les chefs, a la saisie
  // manuelle.
  const { data: chefsData } = useQuery({
    queryKey: ['all-chefs'],
    queryFn: () => equipeService.getAllChefs(),
  });
  const allChefs = chefsData?.chefs;

  // Statistiques
  const { data: stats } = useQuery({
    queryKey: ['vacation-stats', weekDates[0], weekDates[6]],
    queryFn: () => vacationService.getStats(weekDates[0], weekDates[6]),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateVacationDTO) => vacationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-stats'] });
      setShowModal(false);
      toast.success('Vacation creee avec succes');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors de la creation';
      toast.error(message);
    },
  });

  const createPeriodeMutation = useMutation({
    mutationFn: (data: CreateVacationPeriodeDTO) => vacationService.createPeriode(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-stats'] });
      setShowModal(false);
      toast.success(result.message);
      if (result.errors.length > 0) {
        result.errors.forEach((err) => toast.error(err, { duration: 4000 }));
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors de la creation';
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateVacationDTO> }) =>
      vacationService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      setShowModal(false);
      setEditingVacation(null);
      toast.success('Vacation modifiee');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors de la modification';
      toast.error(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => vacationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-stats'] });
      toast.success('Vacation supprimee');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors de la suppression';
      toast.error(message);
    },
  });

  const demarrerMutation = useMutation({
    mutationFn: (id: number) => vacationService.demarrer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-stats'] });
      toast.success('Vacation demarree');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur';
      toast.error(message);
    },
  });

  const terminerMutation = useMutation({
    mutationFn: (id: number) => vacationService.terminer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-stats'] });
      toast.success('Vacation terminee');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur';
      toast.error(message);
    },
  });

  const annulerMutation = useMutation({
    mutationFn: (id: number) => vacationService.annuler(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-stats'] });
      toast.success('Vacation annulee');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur';
      toast.error(message);
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => vacationService.importFromFile(file),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vacations'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-stats'] });
      setShowImportModal(false);
      toast.success(result.message);
      if (result.errors.length > 0) {
        result.errors.forEach((err) => toast.error(err, { duration: 5000 }));
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors de l\'import';
      toast.error(message);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const agentId = Number(formData.get('agent_id'));
    const heureDebut = formData.get('heure_debut') as string;
    const heureFin = formData.get('heure_fin') as string;
    const commentaire = formData.get('commentaire') as string || undefined;

    if (editingVacation) {
      // Mode edition - vacation unique
      const dateDebut = formData.get('date_debut') as string;
      let dateFin = formData.get('date_fin') as string;

      if (!dateFin) {
        dateFin = dateDebut;
      }

      // Detection automatique J+1
      if (dateDebut === dateFin) {
        const [hd, md] = heureDebut.split(':').map(Number);
        const [hf, mf] = heureFin.split(':').map(Number);
        if (hf * 60 + mf < hd * 60 + md) {
          const nextDay = new Date(dateDebut);
          nextDay.setDate(nextDay.getDate() + 1);
          dateFin = nextDay.toISOString().split('T')[0];
        }
      }

      updateMutation.mutate({
        id: editingVacation.id,
        data: { date_debut: dateDebut, date_fin: dateFin, heure_debut: heureDebut, heure_fin: heureFin, commentaire },
      });
    } else if (createMode === 'period') {
      // Mode periode - plusieurs jours
      const periodeDebut = formData.get('periode_debut') as string;
      const periodeFin = formData.get('periode_fin') as string;

      const data: CreateVacationPeriodeDTO = {
        agent_id: agentId,
        periode_debut: periodeDebut,
        periode_fin: periodeFin,
        heure_debut: heureDebut,
        heure_fin: heureFin,
        jours: selectedJours,
        commentaire,
      };

      createPeriodeMutation.mutate(data);
    } else {
      // Mode simple - une seule vacation
      const dateDebut = formData.get('date_debut') as string;
      let dateFin = formData.get('date_fin') as string;

      if (!dateFin) {
        dateFin = dateDebut;
      }

      // Detection automatique J+1
      if (dateDebut === dateFin) {
        const [hd, md] = heureDebut.split(':').map(Number);
        const [hf, mf] = heureFin.split(':').map(Number);
        if (hf * 60 + mf < hd * 60 + md) {
          const nextDay = new Date(dateDebut);
          nextDay.setDate(nextDay.getDate() + 1);
          dateFin = nextDay.toISOString().split('T')[0];
        }
      }

      const data: CreateVacationDTO = {
        agent_id: agentId,
        date_debut: dateDebut,
        date_fin: dateFin,
        heure_debut: heureDebut,
        heure_fin: heureFin,
        commentaire,
      };

      createMutation.mutate(data);
    }
  };

  const handleImport = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const file = formData.get('file') as File;
    if (file) {
      importMutation.mutate(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await vacationService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_vacations.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du telechargement');
    }
  };

  const navigateDate = (direction: number) => {
    const date = new Date(selectedDate);
    if (viewMode === 'day') {
      date.setDate(date.getDate() + direction);
    } else {
      date.setDate(date.getDate() + direction * 7);
    }
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatDateHeader = () => {
    const date = new Date(selectedDate);
    if (viewMode === 'day') {
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } else {
      const start = new Date(weekDates[0]);
      const end = new Date(weekDates[6]);
      return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
  };

  const toggleJour = (jour: number) => {
    setSelectedJours((prev) =>
      prev.includes(jour) ? prev.filter((j) => j !== jour) : [...prev, jour].sort()
    );
  };

  // Handler pour la mise à jour depuis le Gantt (drag & drop)
  const handleGanttUpdate = useCallback((id: number, heureDebut: string, heureFin: string) => {
    // Trouver la vacation pour récupérer les dates
    const vacation = vacations?.find((v) => v.id === id);
    if (!vacation) return;

    let dateDebut = vacation.dateDebut.split('T')[0];
    let dateFin = vacation.dateFin.split('T')[0];

    // Détection automatique J+1 si heure fin < heure début
    const [hd, md] = heureDebut.split(':').map(Number);
    const [hf, mf] = heureFin.split(':').map(Number);

    if (hf * 60 + mf < hd * 60 + md) {
      // Vacation de nuit : date fin = date début + 1 jour
      const nextDay = new Date(dateDebut);
      nextDay.setDate(nextDay.getDate() + 1);
      dateFin = nextDay.toISOString().split('T')[0];
    } else {
      // Vacation normale : même date
      dateFin = dateDebut;
    }

    updateMutation.mutate({
      id,
      data: {
        date_debut: dateDebut,
        date_fin: dateFin,
        heure_debut: heureDebut,
        heure_fin: heureFin,
      },
    });
  }, [vacations, updateMutation]);

  // Trier les vacations par agent puis par date/heure
  const sortedVacations = useMemo(() => {
    if (!vacations) return [];
    return [...vacations].sort((a, b) => {
      // D'abord par nom d'agent
      const nameCompare = a.agent.nomComplet.localeCompare(b.agent.nomComplet);
      if (nameCompare !== 0) return nameCompare;
      // Puis par date
      const dateCompare = a.dateDebut.localeCompare(b.dateDebut);
      if (dateCompare !== 0) return dateCompare;
      // Puis par heure
      return a.heureDebut.localeCompare(b.heureDebut);
    });
  }, [vacations]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement du planning...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Planning des vacations</h2>
          <p className="text-gray-500 text-sm mt-1">Gestion des horaires des agents de deneignement</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary flex items-center"
          >
            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
            Importer
          </button>
          <button
            onClick={() => {
              setEditingVacation(null);
              setCreateMode('single');
              setShowModal(true);
            }}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Nouvelle vacation
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.stats.total}</p>
          </div>
          <div className="bg-blue-50 rounded-lg shadow p-4">
            <p className="text-sm text-blue-600">Planifiees</p>
            <p className="text-2xl font-bold text-blue-700">{stats.stats.planifiees}</p>
          </div>
          <div className="bg-orange-50 rounded-lg shadow p-4">
            <p className="text-sm text-orange-600">En cours</p>
            <p className="text-2xl font-bold text-orange-700">{stats.stats.en_cours}</p>
          </div>
          <div className="bg-green-50 rounded-lg shadow p-4">
            <p className="text-sm text-green-600">Terminees</p>
            <p className="text-2xl font-bold text-green-700">{stats.stats.terminees}</p>
          </div>
          <div className="bg-gray-50 rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Annulees</p>
            <p className="text-2xl font-bold text-gray-600">{stats.stats.annulees}</p>
          </div>
        </div>
      )}

      {/* Navigation et vue */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateDate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <span className="text-lg font-medium min-w-[280px] text-center capitalize">
              {formatDateHeader()}
            </span>
            <button
              onClick={() => navigateDate(1)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              className="ml-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
            >
              Aujourd'hui
            </button>
          </div>

          <div className="flex items-center gap-4">
            {/* Toggle période */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('day')}
                className={`px-3 py-1 text-sm rounded ${viewMode === 'day' ? 'bg-primary-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                <SunIcon className="w-4 h-4 inline mr-1" />
                Jour
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1 text-sm rounded ${viewMode === 'week' ? 'bg-primary-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                Semaine
              </button>
            </div>

            {/* Toggle affichage */}
            <div className="flex items-center gap-2 border-l pl-4">
              <button
                onClick={() => setDisplayMode('gantt')}
                className={`px-3 py-1 text-sm rounded ${displayMode === 'gantt' ? 'bg-primary-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                title="Vue Gantt"
              >
                <ChartBarIcon className="w-4 h-4 inline mr-1" />
                Gantt
              </button>
              <button
                onClick={() => setDisplayMode('table')}
                className={`px-3 py-1 text-sm rounded ${displayMode === 'table' ? 'bg-primary-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                title="Vue Tableau"
              >
                <Bars3Icon className="w-4 h-4 inline mr-1" />
                Tableau
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Planning */}
      {displayMode === 'gantt' ? (
        /* Vue Gantt */
        <GanttChart
          vacations={sortedVacations || []}
          selectedDate={selectedDate}
          onUpdateVacation={handleGanttUpdate}
          viewMode={viewMode}
          weekDates={weekDates}
        />
      ) : (
        /* Vue Tableau */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {sortedVacations && sortedVacations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 w-64">
                      <UserIcon className="w-4 h-4 inline mr-2" />
                      Agent / Chef d'equipe
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Horaires</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Duree</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Statut</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedVacations.map((vacation) => (
                    <tr key={vacation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-primary-600 text-xs font-medium">
                              {vacation.agent.prenom?.[0]}{vacation.agent.nom?.[0]}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-1.5">
                              {vacation.agent.nomComplet}
                              {vacation.agent.roles?.includes('ROLE_CHEF_DENEIGNEMENT') && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded bg-indigo-100 text-indigo-700">
                                  Chef
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{vacation.agent.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {new Date(vacation.dateDebut).toLocaleDateString('fr-FR', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short'
                          })}
                        </div>
                        {vacation.isVacationNuit && (
                          <div className="text-xs text-gray-500">
                            au {new Date(vacation.dateFin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {vacation.isVacationNuit && (
                            <MoonIcon className="w-4 h-4 text-indigo-500" title="Vacation de nuit" />
                          )}
                          <span className="text-gray-900 font-mono text-sm">{vacation.plageHoraire}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {vacation.dureePrevueHeures?.toFixed(1)}h
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${STATUT_COLORS[vacation.statut]}`}
                        >
                          {STATUT_LABELS[vacation.statut]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {vacation.statut === 'planifiee' && (
                            <>
                              <button
                                onClick={() => demarrerMutation.mutate(vacation.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                title="Demarrer"
                              >
                                <PlayIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingVacation(vacation);
                                  setShowModal(true);
                                }}
                                className="p-1.5 text-gray-400 hover:text-primary-500 hover:bg-gray-100 rounded"
                                title="Modifier"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Supprimer cette vacation ?')) {
                                    deleteMutation.mutate(vacation.id);
                                  }
                                }}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded"
                                title="Supprimer"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {vacation.statut === 'en_cours' && (
                            <button
                              onClick={() => terminerMutation.mutate(vacation.id)}
                              className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                              title="Terminer"
                            >
                              <StopIcon className="w-4 h-4" />
                            </button>
                          )}
                          {(vacation.statut === 'planifiee' || vacation.statut === 'en_cours') && (
                            <button
                              onClick={() => {
                                if (confirm('Annuler cette vacation ?')) {
                                  annulerMutation.mutate(vacation.id);
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded"
                              title="Annuler"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune vacation planifiee</p>
              <button
                onClick={() => setShowModal(true)}
                className="mt-4 text-primary-500 hover:text-primary-600"
              >
                Ajouter une vacation
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal creation/edition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingVacation ? 'Modifier la vacation' : 'Planifier une vacation'}
            </h3>

            {/* Toggle mode creation (uniquement en creation) */}
            {!editingVacation && (
              <div className="mb-4 flex rounded-lg bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setCreateMode('single')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    createMode === 'single'
                      ? 'bg-white text-primary-600 shadow'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <SunIcon className="w-4 h-4 inline mr-1" />
                  Jour unique
                </button>
                <button
                  type="button"
                  onClick={() => setCreateMode('period')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    createMode === 'period'
                      ? 'bg-white text-primary-600 shadow'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                  Periode
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Agent</label>
                <select
                  name="agent_id"
                  className="input"
                  required
                  defaultValue={editingVacation?.agent.id || ''}
                  disabled={!!editingVacation}
                >
                  <option value="">Selectionner un agent</option>
                  <optgroup label="Agents">
                    {allAgents?.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.nomComplet} ({agent.email})
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Chefs d'equipe">
                    {allChefs?.map((chef) => (
                      <option key={chef.id} value={chef.id}>
                        {chef.nomComplet} ({chef.email})
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Mode periode */}
              {!editingVacation && createMode === 'period' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Du</label>
                      <input
                        type="date"
                        name="periode_debut"
                        className="input"
                        required
                        defaultValue={selectedDate}
                      />
                    </div>
                    <div>
                      <label className="label">Au</label>
                      <input
                        type="date"
                        name="periode_fin"
                        className="input"
                        required
                        defaultValue={weekDates[6]}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Jours de la semaine</label>
                    <div className="flex gap-2 flex-wrap">
                      {JOURS_SEMAINE.map((jour) => (
                        <button
                          key={jour.value}
                          type="button"
                          onClick={() => toggleJour(jour.value)}
                          className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                            selectedJours.includes(jour.value)
                              ? 'bg-primary-500 text-white border-primary-500'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-primary-500'
                          }`}
                        >
                          {jour.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedJours.length} jour(s) selectionne(s)
                    </p>
                  </div>
                </>
              ) : (
                /* Mode jour unique ou edition */
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Date</label>
                    <input
                      type="date"
                      name="date_debut"
                      className="input"
                      required
                      defaultValue={editingVacation?.dateDebut?.split('T')[0] || selectedDate}
                    />
                  </div>
                  <div>
                    <label className="label">Date fin (optionnel)</label>
                    <input
                      type="date"
                      name="date_fin"
                      className="input"
                      defaultValue={editingVacation?.dateFin?.split('T')[0] || ''}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pour vacation de nuit
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Heure debut</label>
                  <input
                    type="time"
                    name="heure_debut"
                    className="input"
                    required
                    defaultValue={editingVacation?.heureDebut?.substring(0, 5) || '06:00'}
                  />
                </div>
                <div>
                  <label className="label">Heure fin</label>
                  <input
                    type="time"
                    name="heure_fin"
                    className="input"
                    required
                    defaultValue={editingVacation?.heureFin?.substring(0, 5) || '14:00'}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                <MoonIcon className="w-4 h-4 inline mr-2 text-blue-500" />
                <strong>Vacation de nuit:</strong> Si l'heure de fin est inferieure a l'heure de debut
                (ex: 22:00 - 06:00), la vacation se terminera automatiquement le lendemain (J+1).
              </div>

              <div>
                <label className="label">Commentaire (optionnel)</label>
                <textarea
                  name="commentaire"
                  className="input"
                  rows={2}
                  defaultValue={editingVacation?.commentaire || ''}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingVacation(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createMutation.isPending || createPeriodeMutation.isPending || updateMutation.isPending}
                >
                  {editingVacation ? 'Modifier' : createMode === 'period' ? 'Planifier la periode' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal import */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Importer des vacations</h3>
              <Link
                to="/administration/imports"
                className="flex items-center text-sm text-gray-500 hover:text-primary-600"
                title="Voir l'historique complet des imports"
              >
                <DocumentTextIcon className="w-4 h-4 mr-1" />
                Historique des imports
              </Link>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-3">
                Telechargez le modele Excel puis remplissez-le avec les vacations a importer.
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center text-primary-500 hover:text-primary-600"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                Telecharger le modele Excel
              </button>
            </div>

            <form onSubmit={handleImport} className="space-y-4">
              <div>
                <label className="label">Fichier (Excel ou CSV)</label>
                <input
                  type="file"
                  name="file"
                  accept=".xlsx,.xls,.csv"
                  className="input"
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                <strong>Note:</strong> Les vacations avec des chevauchements seront ignorees.
                Les agents doivent exister et avoir le role agent de deneignement.
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary flex items-center"
                  disabled={importMutation.isPending}
                >
                  <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                  {importMutation.isPending ? 'Import en cours...' : 'Importer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanningPage;
